import { defer, mergeMap, Observable, of, retry, Subject, Subscription, tap, } from 'rxjs';
import { createEffectController } from './effectController';
const DEFAULT_MERGE_MAP_PIPELINE = (eventProject) => mergeMap(eventProject);
/**
 * Creates `Effect` from the provided handler.
 *
 * @example
 * ```ts
 * const sumEffect = createEffect<{a: number, b: number}, number>((event) => {
 *   return a + b;
 * });
 * ```
 */
export function createEffect(handler, options) {
    var _a;
    const pipeline = (_a = options === null || options === void 0 ? void 0 : options.pipeline) !== null && _a !== void 0 ? _a : DEFAULT_MERGE_MAP_PIPELINE;
    const event$ = new Subject();
    const controller = createEffectController();
    const subscriptions = new Subscription(() => {
        event$.complete();
        controller.destroy();
    });
    const eventProject = (event) => {
        return defer(() => {
            controller.start();
            const result = handler(event);
            return result instanceof Observable || result instanceof Promise
                ? result
                : of(result);
        }).pipe(tap({
            next: (result) => {
                controller.next({ event, result });
            },
            complete: () => {
                controller.complete();
            },
            error: (error) => {
                controller.error({ origin: 'handler', event, error });
            },
        }));
    };
    subscriptions.add(event$.pipe(pipeline(eventProject), retry()).subscribe());
    return {
        ...controller.state,
        handle(source) {
            const observable = getSourceObservable(source);
            const subscription = observable.subscribe({
                next: (event) => event$.next(event),
                error: (error) => controller.error({ origin: 'source', error }),
            });
            subscriptions.add(subscription);
            return subscription;
        },
        destroy: () => {
            subscriptions.unsubscribe();
        },
    };
}
function getSourceObservable(source) {
    const type = typeof source;
    if (type === 'function' && 'event$' in source) {
        return source.event$;
    }
    if (type === 'object' && 'value$' in source) {
        return source.value$;
    }
    if (source instanceof Observable) {
        return source;
    }
    throw new TypeError('Unexpected source type');
}
//# sourceMappingURL=effect.js.map